/**
 * One chat turn's stream handling, as plain functions over the shared refs.
 * Split out of `useAgentChat.ts`'s `sendMessage` with no behavior change; the
 * dispatch that routes frames here stays in `sendMessage` (`useAgentChatTurn.ts`).
 */
import type { QueryClient } from '@tanstack/react-query';
import type { ConnectionSummary } from '@bower/shared';

import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { useSpreadsheetGridStore } from '../../stores/spreadsheetGridStore';
import { maybeRecordAhaMoment } from './ahaMoment';
import { applySpreadsheetUndo, isRefusalNotice, reduceChatMessage } from './agentChatReducer';
import type { SessionHistoryResult, SSEEvent } from '../../services/agentChat.service';
import { isStoppingOwnTurn, type ChatCore } from './useAgentChatCore';

/** Agent undo outcomes after which the open grid may be stale. */
const REMOUNT_OUTCOMES: ReadonlySet<string> = new Set(['undone', 'SPREADSHEET_UNDO_UNCONFIRMED']);

/** What a turn's terminal handling needs to know about the turn. */
export interface ChatTurn {
  readonly chat: ChatCore;
  /** The session the turn was sent on. */
  readonly sessionId: string;
  readonly assistantId: string;
  readonly turnRowIds: readonly string[];
  readonly sendStartTime: number;
  readonly contextType: string | undefined;
  readonly invalidateHistory: () => void;
  /**
   * What a refusal gives back: the researcher's row to take out of the
   * transcript and the message to return to the composer. `null` for a HITL
   * reply, which has neither.
   */
  readonly refusable: RefusableMessage | null;
}

/**
 * The refused message's hand-back. On the upload-first path the files that
 * uploaded are already notes in the workspace, so they are withheld (handing
 * them back would import them a second time on the re-send) and `savedFiles`
 * counts them for the notice; `files` is then only the ones that failed, which
 * were never stored. On the bytes path nothing was stored, so every file goes
 * back with the text.
 */
export interface RefusableMessage {
  readonly userMessageId: string;
  readonly text: string;
  files?: readonly File[];
  savedFiles?: { readonly saved: number };
}

/** The sentence a refusal notice adds when files were already saved. */
export function savedFilesNote(saved: { readonly saved: number }): string {
  return saved.saved === 1
    ? 'Your file was saved to the workspace.'
    : `Your ${saved.saved} files were saved to the workspace.`;
}

/**
 * Drop the cached transcript for this session, on EVERY terminal outcome.
 *
 * The first history request can race the streamed response and cache an
 * empty transcript for its full stale window, and this hook is re-seeded
 * whenever it re-mounts (a page reload) or changes session (the history
 * menu, a workspace switch) — so without this a re-seed would reuse that
 * entry until the window expired. Collapsing the chat panel is NOT one of
 * those any more: the session lives in AgentChatProvider, above the panel
 * that unmounts.
 *
 * `done` alone is not enough, and the gap is destructive rather than
 * cosmetic. A confirmation reply resumes a paused tool call: ADK appends
 * the response and RUNS the tool before anything downstream can fail, so
 * a turn that dies after that (stream error, dropped connection) has
 * already merged the notes. Leaving the pre-reply transcript cached means
 * a remount inside the stale window re-offers the prompt for a call that
 * already ran — and ADK re-executes on a second answer, deleting a second
 * set of notes. Every exit from a turn re-asks the server instead.
 */
export function historyInvalidator(
  queryClient: QueryClient,
  workspaceId: string,
  sessionId: string,
): () => void {
  return () => {
    markOwnTurnEnded(queryClient, workspaceId, sessionId);
    void queryClient.invalidateQueries({
      queryKey: ['session-history', workspaceId, sessionId],
    });
  };
}

/**
 * This tab's own turn is over, so a cached history read that said a turn was
 * running was about THIS turn, and is now false.
 *
 * Without it, a read taken while this tab streamed (a remount, a staleTime
 * refetch) keeps `turnInProgress: true` in the cache, and the moment
 * `isStreaming` clears the chat reads it as someone ELSE's turn: composer
 * locked, activity line on, and the 10-minute wait clock started from that
 * stale read, until the next refetch lands. Written on every exit of this
 * tab's turn (`done`, `error`, a rejected send, a refusal, and Stop), before
 * any refetch. A refusal's refetch then reports the other request's turn
 * afresh.
 */
export function markOwnTurnEnded(
  queryClient: QueryClient,
  workspaceId: string,
  sessionId: string,
): void {
  queryClient.setQueryData<SessionHistoryResult>(
    ['session-history', workspaceId, sessionId],
    (old) => (old?.turnInProgress ? { ...old, turnInProgress: false } : old),
  );
}

/** A `spreadsheet_undo` frame. */
export function onTurnSpreadsheetUndo(turn: ChatTurn, event: SSEEvent): void {
  turn.chat.setMessages((prev) => applySpreadsheetUndo(prev, event));
  // Only these can have changed the workbook: a confirmed undo, and
  // an unconfirmed one (it may have reverted). No analytics here:
  // the backend records this undo with `trigger: 'tool'`.
  if (REMOUNT_OUTCOMES.has(event.data.outcome ?? '') && event.data.artifactId) {
    useSpreadsheetGridStore.getState().remount(event.data.artifactId);
  }
}

/** A `done` frame: the service took the turn. */
export function onTurnDone(turn: ChatTurn, event: SSEEvent): void {
  const { chat, assistantId, turnRowIds } = turn;
  chat.setMessages((prev) =>
    prev.map((msg) => (msg.id === assistantId ? reduceChatMessage(msg, event) : msg)),
  );
  safeAnalytics.track(ANALYTICS_EVENTS.CHAT_RESPONSE_RECEIVED, {
    response_time_ms: Date.now() - turn.sendStartTime,
    success: true,
    context_type: turn.contextType,
  });
  void maybeRecordAhaMoment();
  chat.currentAssistantIdRef.current = null;
  // The service took this turn, so the transcript it hands back
  // contains it. Acknowledged: its rows stop being ours to keep.
  for (const id of turnRowIds) chat.unacknowledgedRowIdsRef.current.delete(id);
  chat.currentTurnRowIdsRef.current = [];
  chat.setIsStreaming(false);
  chat.setCurrentAgent(null);
  turn.invalidateHistory();
}

/**
 * The `code` the agent service puts on the `error` frame when it turns a send
 * away because the session is still running an earlier turn (one turn per
 * session, `services/chat_turn_lock.py`). Mirrors `TURN_IN_PROGRESS_CODE` in
 * `routes/chat_errors.py`.
 */
export const TURN_IN_PROGRESS_CODE = 'turn_in_progress';

/** The server's refusal copy, for a frame that arrives without its message. */
export const REFUSAL_FALLBACK_MESSAGE =
  "Still working on your last message, so this one wasn't sent. Try again in a moment.";

/**
 * The refusal copy when the turn still running is the one this tab just
 * stopped (`isStoppingOwnTurn`): it is this tab's own, so not "working".
 */
export const STOPPING_REFUSAL_MESSAGE = 'Still stopping your last message. Send again in a moment.';

/**
 * The server refused this send: an earlier turn on the session is still
 * running, on another request (another tab or device).
 *
 * Not a failure, so not red and not CHAT_RESPONSE_FAILED: the placeholder
 * becomes a calm status line carrying the server's message, and the history
 * read that follows reports the running turn, which puts the chat into its
 * still-working state until the answer lands (`useAgentChatHistory`).
 *
 * The message was NEVER SENT, so it must not sit in the transcript looking
 * sent (and then vanish on a reload, which reads as queued). Its row comes
 * out, and its text goes back to the composer (`refusedDraft`), with its files
 * only when they were not already uploaded (see `RefusableMessage`); when they
 * were, the notice says so instead.
 *
 * The notice row stays UNACKNOWLEDGED while the other turn runs, so the
 * reseeds that follow it keep the notice; the snapshot that shows nothing
 * running drops it (`refusalPendingRef` makes sure that snapshot reseeds).
 */
function refuseTurn(turn: ChatTurn, event: SSEEvent): void {
  const { chat, assistantId, refusable } = turn;
  if (refusable) {
    chat.setRefusedDraft({ text: refusable.text, files: refusable.files, ts: Date.now() });
  }
  const filesNote = refusable?.savedFiles ? savedFilesNote(refusable.savedFiles) : '';
  // A Stop and an immediate resend: the server still holds the session for
  // the stopped turn until its run has unwound (`stopAgentTurn`).
  const stopping = isStoppingOwnTurn(chat.stoppedOwnTurnRef.current, turn.sessionId, Date.now());
  const serverMessage = event.data.message || REFUSAL_FALLBACK_MESSAGE;
  const message = stopping ? STOPPING_REFUSAL_MESSAGE : serverMessage;
  chat.refusalPendingRef.current = true;
  chat.setMessages((prev) =>
    prev
      // One notice at a time: a second refusal replaces the first.
      .filter((msg) => msg.id !== refusable?.userMessageId && !isRefusalNotice(msg))
      .map((msg) =>
        msg.id === assistantId
          ? {
              id: msg.id,
              role: 'status',
              statusKind: 'info',
              content: filesNote ? `${message} ${filesNote}` : message,
              timestamp: msg.timestamp,
            }
          : msg,
      ),
  );
  safeAnalytics.track(ANALYTICS_EVENTS.CHAT_TURN_STILL_WORKING_SHOWN, {
    reason: stopping ? 'stopping' : 'refused',
  });
  chat.currentAssistantIdRef.current = null;
  chat.currentTurnRowIdsRef.current = [];
  chat.setIsStreaming(false);
  chat.setCurrentAgent(null);
  turn.invalidateHistory();
}

/** An `error` frame, delivered by the stream or raised before it opened. */
export function onTurnError(turn: ChatTurn, event: SSEEvent): void {
  if (event.data.code === TURN_IN_PROGRESS_CODE) {
    refuseTurn(turn, event);
    return;
  }
  const { chat, assistantId, turnRowIds } = turn;
  chat.setMessages((prev) =>
    prev.map((msg) =>
      msg.id === assistantId
        ? {
            ...reduceChatMessage(msg, event),
            content: msg.content || event.data.message || 'An error occurred',
          }
        : msg,
    ),
  );
  safeAnalytics.track(ANALYTICS_EVENTS.CHAT_RESPONSE_FAILED, {
    response_time_ms: Date.now() - turn.sendStartTime,
    success: false,
    error_type: 'stream_error',
    context_type: turn.contextType,
  });
  chat.currentAssistantIdRef.current = null;
  // Only a DELIVERED error acknowledges the turn. `sendAgentMessage`
  // raises this same event type for `Not authenticated` and
  // `Request failed: <status>` before the stream opens, and those
  // never reached the route — treating them as acknowledgement is
  // what deleted the researcher's question and the record that it
  // failed. A stream error carries no flag and does acknowledge.
  if (!event.data.undelivered) {
    for (const id of turnRowIds) chat.unacknowledgedRowIdsRef.current.delete(id);
    chat.currentTurnRowIdsRef.current = [];
  }
  chat.setIsStreaming(false);
  chat.setCurrentAgent(null);
  turn.invalidateHistory();
}

/** The send rejected outright: the turn never left the browser. */
export function failRejectedTurn(turn: ChatTurn): void {
  const { chat, assistantId } = turn;
  safeAnalytics.track(ANALYTICS_EVENTS.CHAT_RESPONSE_FAILED, {
    response_time_ms: Date.now() - turn.sendStartTime,
    success: false,
    error_type: 'connection_error',
    context_type: turn.contextType,
  });
  chat.setIsStreaming(false);
  // The `done` and `error` branches both clear this, and so must the
  // rejection: a turn that never left the browser is not in flight. Left
  // set, it points at a placeholder that is already an error row, so the
  // next `cancelMessage` appends `_[cancelled]_` over the only record
  // that the send failed. The rows stay UNACKNOWLEDGED either way — that
  // is `unacknowledgedRowIdsRef`'s job, and a send the service never took
  // is exactly what it must keep.
  chat.currentAssistantIdRef.current = null;
  // Deliberately NOT removed from `unacknowledgedRowIdsRef`: the service
  // never took this turn, so no snapshot will ever carry it and the seed
  // must keep it. Dropping it from the CURRENT-turn ref is what stops a
  // later `cancelMessage` releasing it anyway.
  chat.currentTurnRowIdsRef.current = [];
  chat.setMessages((prev) =>
    prev.map((msg) =>
      msg.id === assistantId
        ? {
            ...msg,
            deliveryStatus: 'error',
            content: 'Failed to connect to agent service',
          }
        : msg,
    ),
  );
  turn.invalidateHistory();
}

export interface ConnectorPickInputs {
  readonly connections: readonly ConnectionSummary[] | undefined;
  readonly connectorsEnabled: boolean;
  readonly workspaceId: string;
  readonly enabledFromActive: (
    workspaceId: string,
    activeToolkitKeys: ReadonlyArray<string>,
  ) => ReadonlyArray<string>;
  readonly webSearchEnabled: boolean;
}

/**
 * Composer tool picker — both fields only flow when the
 * workspace is non-PHI (PHI workspaces have no Composio
 * attachments at all so the picker UI is hidden and the
 * wire fields are stripped).
 *
 * When `enabledConnectorToolkits` is undefined the form
 * omits the field; the agent then calls
 * `composio.connected_accounts.list(user_ids=[uid],
 * statuses=['ACTIVE'])` server-side (see
 * composio_tools._enumerate_active_toolkits) and uses ALL
 * returned toolkits. So undefined here means "use whatever's
 * active", explicitly populated means "use this subset".
 */
export function connectorPicks(inputs: ConnectorPickInputs): {
  readonly enabledConnectorToolkits: ReadonlyArray<string> | undefined;
  readonly webSearchEnabled: boolean | undefined;
} {
  const { connections, connectorsEnabled, workspaceId } = inputs;
  const activeKeys = (connections ?? [])
    .filter((c) => c.status === 'ACTIVE')
    .map((c) => c.toolkitKey);
  const enabledConnectorToolkits =
    !connectorsEnabled || activeKeys.length === 0 || !workspaceId
      ? undefined
      : inputs.enabledFromActive(workspaceId, activeKeys);
  const webSearchEnabled = connectorsEnabled ? inputs.webSearchEnabled : undefined;
  return { enabledConnectorToolkits, webSearchEnabled };
}
